import { ResponseGetPogAnalysis_OutputStorePogId_ } from "@CommonApi/pog-analysis/_outputStorePogId_";
import { Modal, Flex, Text, Scroller, Grid, Icon } from "src/elements";
import { Color } from "src/elements";
import { Tooltip } from "src/components";
import { dateIsoStringToLocaleString } from "src/utils";
import { useState } from "react";
import { SectionHeader } from "src/components/FiltersSidebar/components/SectionHeader";

export const PogSummaryModalInner = ({
  pogSummaryData,
}: {
  pogSummaryData: ResponseGetPogAnalysis_OutputStorePogId_;
}) => {
  const {
    analysis_summary,
    recommendations,
    generated_at,
    output_store_pog_id,
    total_expected_sales,
    uplift,
  } = pogSummaryData;

  // Format the total expected sales with currency formatting
  const formattedSales = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(total_expected_sales);

  // Format uplift value with 1 decimal place, + prefix for positive, and % symbol
  const formatUplift = (value: number): string => {
    const formatted = Math.abs(value).toFixed(1);
    const sign = value > 0 ? "+" : value < 0 ? "-" : "";
    return `${sign}${formatted}%`;
  };

  const formattedUplift = formatUplift(uplift);

  // Determine color based on uplift value
  const upliftColor = uplift > 0 ? Color.success : uplift < 0 ? Color.danger : Color.textSecondary;

  // Determine icon based on uplift value
  const upliftIcon = uplift > 0 ? "arrowUp" : uplift < 0 ? "arrowDown" : undefined;

  // CDT Distribution section collapse state
  const [isCdtDistributionExpanded, setIsCdtDistributionExpanded] = useState(true);

  return (
    <Modal
      name="PogSummaryModal"
      title={`🤖 AI-Powered POG Intelligence Report - POG #${output_store_pog_id}`}
      width="1000px"
    >
      <Flex column padding="20px" gap="20px" minHeight="0">
        <Scroller>
          <Flex column gap="20px">
            {/* Total Expected Sales Header */}
            <Flex column gap="10px" padding="20px" background="#6B7BA8" borderRadius="8px">
              <Flex align="center" gap="10px" justify="space-between">
                <Flex align="center" gap="10px">
                  <Icon name="checkmarkCircle" size={28} color={Color.white} />
                  <Text variant="h3" color={Color.white} bold>
                    Total Expected Sales (Ranged Items)
                  </Text>
                </Flex>
                <Text variant="h2" color={Color.white} bold>
                  {formattedSales}
                </Text>
              </Flex>
              <Flex align="center" gap="8px" justify="right">
                <Flex align="center" gap="6px">
                  <Text variant="body3" color={Color.white}>
                    Sales Uplift:
                  </Text>
                  <Flex padding="0 3px" align="center">
                    <Icon name="info" size={12} color={Color.white} />
                    <Tooltip position="left" width="250px" textAlign="left">
                      Percentage change in total expected sales compared to the most recent previous
                      POG for this store in the same merchflow. A positive value indicates
                      improvement, while a negative value indicates a decline.
                    </Tooltip>
                  </Flex>
                </Flex>
                <Flex align="center" gap="6px">
                  {upliftIcon && <Icon name={upliftIcon} size={16} color={upliftColor} />}
                  <Text variant="h4" color={upliftColor} bold>
                    {formattedUplift}
                  </Text>
                </Flex>
              </Flex>
            </Flex>

            {/* POG Overview Section */}
            <Flex column gap="10px" padding="16px" background={Color.lightGray} borderRadius="8px">
              <Flex align="center" gap="10px">
                <Icon name="info" size={24} color={Color.textMain} />
                <Text variant="h3" color={Color.primary} bold>
                  POG Overview
                </Text>
              </Flex>
              <Text variant="subtitle2">{analysis_summary.pog_overview}</Text>
            </Flex>

            {/* Analysis Summary Section */}
            <Flex column gap="15px">
              <Flex align="center" gap="10px">
                <Icon name="summary" size={24} color={Color.primary} />
                <Text variant="h3" color={Color.primary} bold>
                  Analysis Summary
                </Text>
              </Flex>

              {/* Basic Metrics */}
              <Flex
                column
                gap="10px"
                padding="16px"
                background={Color.lightGray}
                borderRadius="8px"
              >
                <Flex justify="space-between">
                  <Text variant="subtitle1" bold>
                    Efficiency Score:
                  </Text>
                  <Text variant="subtitle1">{analysis_summary.efficiency_score}%</Text>
                </Flex>
                <Flex justify="space-between">
                  <Text variant="subtitle1" bold>
                    Space Utilization:
                  </Text>
                  <Text variant="subtitle1">{analysis_summary.space_utilization.toFixed(1)}%</Text>
                </Flex>
                <Flex justify="space-between">
                  <Text variant="subtitle1" bold>
                    Total Items:
                  </Text>
                  <Text variant="subtitle1">{analysis_summary.total_items}</Text>
                </Flex>
                <Flex justify="space-between">
                  <Text variant="subtitle1" bold>
                    Total Bays:
                  </Text>
                  <Text variant="subtitle1">{analysis_summary.total_bays}</Text>
                </Flex>
                <Flex justify="space-between">
                  <Text variant="subtitle1" bold>
                    Total Shelves:
                  </Text>
                  <Text variant="subtitle1">{analysis_summary.total_shelves}</Text>
                </Flex>
              </Flex>

              {/* CDT Distribution Table */}
              <Flex
                column
                gap="10px"
                padding="16px"
                background={Color.lightGray}
                borderRadius="8px"
              >
                <SectionHeader
                  title="CDT Distribution - Space Mix Analysis:"
                  isExpanded={isCdtDistributionExpanded}
                  setIsExpanded={setIsCdtDistributionExpanded}
                  textVariant="subtitle1"
                />

                {isCdtDistributionExpanded && (
                  <>
                    {/* Table Header */}
                    <Grid
                      columns="2fr 1fr 1fr 1fr 1fr 1fr 1fr"
                      gap="8px"
                      padding="8px"
                      background={Color.white}
                      borderRadius="4px"
                    >
                      <Text variant="body3" color={Color.textSecondary} bold>
                        CDTO
                      </Text>
                      <Text variant="body3" color={Color.textSecondary} bold>
                        Sales Mix %
                      </Text>
                      <Text variant="body3" color={Color.textSecondary} bold>
                        Space Mix %
                      </Text>
                      <Text variant="body3" color={Color.textSecondary} bold>
                        Current Width %
                      </Text>
                      <Text variant="body3" color={Color.textSecondary} bold>
                        % Delta
                      </Text>
                      <Text variant="body3" color={Color.textSecondary} bold>
                        Threshold
                      </Text>
                      <Text variant="body3" color={Color.textSecondary} bold>
                        Met Threshold (1/0)
                      </Text>
                    </Grid>

                    {/* Table Rows */}
                    {analysis_summary.cdt_distribution.segments.map((segment, index) => (
                      <Grid
                        key={index}
                        columns="2fr 1fr 1fr 1fr 1fr 1fr 1fr"
                        gap="8px"
                        padding="8px"
                        background={Color.white}
                        borderRadius="4px"
                      >
                        <Text variant="body3">{segment.segment}</Text>
                        <Text variant="body3">{segment.sales_mix_pct}%</Text>
                        <Text variant="body3">{segment.space_mix_pct}%</Text>
                        <Text variant="body3">{segment.current_width_pct}%</Text>
                        <Text variant="body3">{segment.percentage_delta}%</Text>
                        <Text variant="body3">{segment.threshold}%</Text>
                        <Text variant="body3">{segment.threshold_met}</Text>
                      </Grid>
                    ))}

                    {/* Summary Row */}
                    <Grid
                      columns="2fr 1fr 1fr 1fr 1fr 1fr 1fr"
                      gap="8px"
                      padding="8px"
                      background={Color.primary}
                      borderRadius="4px"
                    >
                      <Text variant="body3" color={Color.white} bold>
                        Total
                      </Text>
                      <Text variant="body3" color={Color.white} bold>
                        {analysis_summary.cdt_distribution.segments
                          .reduce((sum, seg) => sum + seg.sales_mix_pct, 0)
                          .toFixed(1)}
                        %
                      </Text>
                      <Text variant="body3" color={Color.white} bold>
                        {analysis_summary.cdt_distribution.segments
                          .reduce((sum, seg) => sum + seg.space_mix_pct, 0)
                          .toFixed(1)}
                        %
                      </Text>
                      <Text variant="body3" color={Color.white} bold>
                        {analysis_summary.cdt_distribution.segments
                          .reduce((sum, seg) => sum + seg.current_width_pct, 0)
                          .toFixed(1)}
                        %
                      </Text>
                      <Text variant="body3" color={Color.white} bold>
                        -
                      </Text>
                      <Text variant="body3" color={Color.white} bold>
                        -
                      </Text>
                      <Text variant="body3" color={Color.white} bold>
                        {analysis_summary.cdt_distribution.space_mix_compliance.toFixed(1)} Space
                        Mix Compliance
                      </Text>
                    </Grid>

                    {/* Compliance Summary */}
                    <Flex
                      column
                      gap="5px"
                      padding="8px"
                      background={Color.white}
                      borderRadius="4px"
                    >
                      <Text variant="body3" bold>
                        Compliance Summary:
                      </Text>
                      <Text variant="body3">
                        Segments meeting threshold:{" "}
                        {analysis_summary.cdt_distribution.segments_meeting_threshold} /{" "}
                        {analysis_summary.cdt_distribution.total_segments}
                      </Text>
                      <Text variant="body3">
                        Threshold: {analysis_summary.cdt_distribution.threshold_percentage}%
                      </Text>
                      <Text variant="body3">
                        Overall compliance:{" "}
                        {(analysis_summary.cdt_distribution.space_mix_compliance * 100).toFixed(1)}%
                      </Text>
                    </Flex>
                  </>
                )}
              </Flex>

              {/* Evaluation Criteria */}
              <Flex
                column
                gap="10px"
                padding="16px"
                background={Color.lightGray}
                borderRadius="8px"
              >
                <Text variant="subtitle1" color={Color.textMain} bold>
                  Evaluation Criteria:
                </Text>
                <Flex column gap="5px">
                  <Flex justify="space-between">
                    <Text variant="body3">Space Utilization Score:</Text>
                    <Text variant="body3">
                      {analysis_summary.evaluation_criteria.space_utilization_score}%
                    </Text>
                  </Flex>
                  <Flex justify="space-between">
                    <Text variant="body3">CDT Distribution Score:</Text>
                    <Text variant="body3">
                      {analysis_summary.evaluation_criteria.cdt_distribution_score}%
                    </Text>
                  </Flex>
                  <Flex justify="space-between">
                    <Text variant="body3">Performance Target Score:</Text>
                    <Text variant="body3">
                      {analysis_summary.evaluation_criteria.performance_target_score}%
                    </Text>
                  </Flex>
                  <Flex justify="space-between">
                    <Text variant="body3">Constraint Compliance Score:</Text>
                    <Text variant="body3">
                      {analysis_summary.evaluation_criteria.constraint_compliance_score}%
                    </Text>
                  </Flex>
                </Flex>
              </Flex>

              {/* Efficiency Drivers */}
              <Flex
                column
                gap="10px"
                padding="16px"
                background={Color.lightGray}
                borderRadius="8px"
              >
                <Text variant="subtitle1" color={Color.textMain} bold>
                  Efficiency Drivers:
                </Text>
                <Text variant="body3">{analysis_summary.efficiency_drivers}</Text>
              </Flex>

              {/* Optimization Potential */}
              <Flex
                column
                gap="10px"
                padding="16px"
                background={Color.lightGray}
                borderRadius="8px"
              >
                <Text variant="subtitle1" color={Color.textMain} bold>
                  Optimization Potential:
                </Text>
                <Text variant="body3">{analysis_summary.optimization_potential}</Text>
              </Flex>
            </Flex>

            {/* Key Strengths Table */}
            <Flex column gap="15px">
              <Flex align="center" gap="10px">
                <Icon name="thumbsUp" size={24} color={Color.success} />
                <Text variant="h3" color={Color.success} bold>
                  Key Strengths
                </Text>
              </Flex>
              <Flex column gap="8px">
                {analysis_summary.key_strengths.map((strength, index) => (
                  <Flex
                    key={index}
                    column
                    gap="6px"
                    padding="12px"
                    background={Color.lightGray}
                    borderRadius="4px"
                  >
                    <Text variant="subtitle1" color={Color.textMain} bold>
                      {strength.title}
                    </Text>
                    <Text variant="body3">{strength.description}</Text>
                    <Text variant="body3" color={Color.textSecondary}>
                      <Text variant="body3" color={Color.textSecondary} bold>
                        Impact:
                      </Text>{" "}
                      {strength.impact}
                    </Text>
                  </Flex>
                ))}
              </Flex>
            </Flex>

            {/* Critical Weaknesses Table */}
            <Flex column gap="15px">
              <Flex align="center" gap="10px">
                <Icon name="alert" size={24} color={Color.danger} />
                <Text variant="h3" color={Color.danger} bold>
                  Critical Weaknesses
                </Text>
              </Flex>
              <Flex column gap="8px">
                {analysis_summary.critical_weaknesses.map((weakness, index) => (
                  <Flex
                    key={index}
                    column
                    gap="6px"
                    padding="12px"
                    background={Color.lightGray}
                    borderRadius="4px"
                  >
                    <Text variant="subtitle1" color={Color.textMain} bold>
                      {weakness.title}
                    </Text>
                    <Text variant="body3">{weakness.description}</Text>
                    <Text variant="body3" color={Color.textSecondary}>
                      <Text variant="body3" color={Color.textSecondary} bold>
                        Impact:
                      </Text>{" "}
                      {weakness.impact}
                    </Text>
                  </Flex>
                ))}
              </Flex>
            </Flex>

            {/* Recommendations Section */}
            <Flex column gap="15px">
              <Flex align="center" gap="10px">
                <Icon name="ai" size={24} color={Color.yellow} />
                <Text variant="h3" color={Color.yellow} bold>
                  Recommendations
                </Text>
              </Flex>
              <Flex column gap="20px">
                {recommendations.map((category, categoryIndex) => (
                  <Flex
                    key={categoryIndex}
                    column
                    gap="12px"
                    padding="16px"
                    background={Color.lightGray}
                    borderRadius="8px"
                  >
                    <Text variant="subtitle1" color={Color.textMain} bold>
                      {category.priority} - {category.category.replace("_", " ").toUpperCase()}
                    </Text>

                    {category.items.map((item, itemIndex) => (
                      <Grid
                        key={itemIndex}
                        columns="1fr 2fr"
                        gap="8px"
                        padding="12px"
                        background={Color.white}
                        borderRadius="4px"
                      >
                        {/* Title Row */}
                        {item.title && (
                          <>
                            <Text variant="body3" color={Color.textSecondary} bold>
                              TITLE:
                            </Text>
                            <Text variant="body3">{item.title}</Text>
                          </>
                        )}

                        {/* What Row */}
                        {item.what && (
                          <>
                            <Text variant="body3" color={Color.textSecondary} bold>
                              WHAT:
                            </Text>
                            <Text variant="body3">{item.what}</Text>
                          </>
                        )}

                        {/* Where Row */}
                        {item.where && (
                          <>
                            <Text variant="body3" color={Color.textSecondary} bold>
                              WHERE:
                            </Text>
                            <Text variant="body3">{item.where}</Text>
                          </>
                        )}

                        {/* Current State Row */}
                        {item.current_state && (
                          <>
                            <Text variant="body3" color={Color.textSecondary} bold>
                              CURRENT STATE:
                            </Text>
                            <Text variant="body3">{item.current_state}</Text>
                          </>
                        )}

                        {/* Proposed State Row */}
                        {item.proposed_state && (
                          <>
                            <Text variant="body3" color={Color.textSecondary} bold>
                              PROPOSED STATE:
                            </Text>
                            <Text variant="body3">{item.proposed_state}</Text>
                          </>
                        )}

                        {/* Why Row */}
                        {item.why && (
                          <>
                            <Text variant="body3" color={Color.textSecondary} bold>
                              WHY:
                            </Text>
                            <Text variant="body3">{item.why}</Text>
                          </>
                        )}

                        {/* Expected Result Row */}
                        {item.expected_result && (
                          <>
                            <Text variant="body3" color={Color.textSecondary} bold>
                              EXPECTED RESULT:
                            </Text>
                            <Text variant="body3">{item.expected_result}</Text>
                          </>
                        )}

                        {/* Implementation Complexity Row */}
                        {item.implementation_complexity && (
                          <>
                            <Text variant="body3" color={Color.textSecondary} bold>
                              IMPLEMENTATION COMPLEXITY:
                            </Text>
                            <Text variant="body3">{item.implementation_complexity}</Text>
                          </>
                        )}

                        {/* Item Name Row */}
                        {item.item_name && (
                          <>
                            <Text variant="body3" color={Color.textSecondary} bold>
                              ITEM NAME:
                            </Text>
                            <Text variant="body3">{item.item_name}</Text>
                          </>
                        )}

                        {/* Current Location Row */}
                        {item.current_location && (
                          <>
                            <Text variant="body3" color={Color.textSecondary} bold>
                              CURRENT LOCATION:
                            </Text>
                            <Text variant="body3">{item.current_location}</Text>
                          </>
                        )}

                        {/* Current Facings Row */}
                        {item.current_facings !== undefined && (
                          <>
                            <Text variant="body3" color={Color.textSecondary} bold>
                              CURRENT FACINGS:
                            </Text>
                            <Text variant="body3">{item.current_facings}</Text>
                          </>
                        )}

                        {/* Recommended Facings Row */}
                        {item.recommended_facings !== undefined && (
                          <>
                            <Text variant="body3" color={Color.textSecondary} bold>
                              RECOMMENDED FACINGS:
                            </Text>
                            <Text variant="body3">{item.recommended_facings}</Text>
                          </>
                        )}

                        {/* Reason Row */}
                        {item.reason && (
                          <>
                            <Text variant="body3" color={Color.textSecondary} bold>
                              REASON:
                            </Text>
                            <Text variant="body3">{item.reason}</Text>
                          </>
                        )}

                        {/* Expected Impact Row */}
                        {item.expected_impact && (
                          <>
                            <Text variant="body3" color={Color.textSecondary} bold>
                              EXPECTED IMPACT:
                            </Text>
                            <Text variant="body3">{item.expected_impact}</Text>
                          </>
                        )}
                      </Grid>
                    ))}
                  </Flex>
                ))}
              </Flex>
            </Flex>

            {/* Footer Info */}
            <Flex justify="left" padding="8px 0">
              <Text variant="body3" color={Color.textSecondary}>
                Generated: {dateIsoStringToLocaleString(generated_at)}
              </Text>
            </Flex>
          </Flex>
        </Scroller>
      </Flex>
    </Modal>
  );
};
